import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lightbulb, MessageCircle, CornerDownRight, Send, ChefHat } from "lucide-react";
import { api, formatError } from "../../lib/api";
import { LoadingState } from "../states";
import { toast } from "sonner";

function Reply({ item, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    const url = item.target === "recipe" ? `/recipes/${item.recipe_id}/interactions` : `/chefs/${item.chef_id}/interactions`;
    try {
      await api.post(url, { type: item.type, content: text.trim(), parent_id: item.parent_id || item.id });
      toast.success("Reply sent"); setText(""); onDone();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 flex gap-2">
      <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid={`inbox-reply-input-${item.id}`} placeholder="Reply as chef…"
        className="flex-1 min-w-0 h-11 rounded-xl border border-border bg-white px-4 text-base outline-none focus:border-coral focus:ring-4 focus:ring-coral/10 font-medium" />
      <button onClick={send} disabled={busy || !text.trim()} data-testid={`inbox-reply-send-${item.id}`} className="inline-flex items-center gap-1.5 h-11 px-4 rounded-full bg-coral hover:bg-coral-hover text-white text-sm font-bold disabled:opacity-50"><Send size={14} /> Send</button>
    </div>
  );
}

export function KitchenInteractions() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/kitchen/interactions").then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  if (items === null) return <LoadingState label="Loading conversations…" />;
  const list = items.filter((i) => filter === "all" || i.type === filter);
  return (
    <div data-testid="kitchen-interactions">
      <div className="flex gap-2 mb-4">
        {[["all", "All"], ["comment", "Comments"], ["suggestion", "Suggestions"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`inbox-filter-${k}`} className={`rounded-full px-4 h-10 text-sm font-bold ${filter === k ? "bg-ink text-white" : "bg-white border border-border text-ink-soft hover:border-coral"}`}>{l}</button>
        ))}
      </div>
      {list.length === 0 ? (
        <div className="rounded-3xl bg-white border border-border p-10 text-center text-ink-soft">No {filter === "all" ? "conversations" : filter + "s"} yet. When people comment on your recipes or profile, they'll show up here.</div>
      ) : (
        <div className="space-y-3">
          {list.map((i) => (
            <div key={i.id} className="rounded-2xl bg-white border border-border p-4" data-testid={`inbox-item-${i.id}`}>
              <div className="flex items-center gap-2 flex-wrap text-xs font-bold text-ink-soft">
                {i.type === "suggestion" ? <Lightbulb size={14} className="text-amber-600" /> : <MessageCircle size={14} className="text-coral" />}
                <span className="text-ink">{i.user?.name}</span>{i.is_chef && <span className="inline-flex items-center gap-1 text-coral"><ChefHat size={12} /> you</span>}
                {i.parent_id && <span className="inline-flex items-center gap-1"><CornerDownRight size={12} /> reply</span>}
                <span>· on</span>
                <Link to={i.target_link} className="text-coral hover:underline truncate max-w-[200px]">{i.target_title}</Link>
                <span className="ml-auto">{new Date(i.created_at).toLocaleString()}</span>
              </div>
              <p className="mt-2 text-ink leading-relaxed whitespace-pre-line break-words">{i.content}</p>
              {!i.is_chef && <Reply item={i} onDone={load} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
